<template>
    <div class="custom-blockui-container">
        <nav v-if="stek?.identity && mode === 'business'" class="strategy-nav">
            <div class="nav-group-label">Strategy</div>
            <router-link
                v-for="item in strategyNav"
                :key="item.name"
                :to="withThreadRevision({ name: item.name, params: navParams })"
                class="nav-item"
                :class="[
                    { 'nav-item--active': route.name === item.name },
                    strategyDiffClasses(item.diffKey),
                ]"
            >
                <span class="nav-item__icon" :class="item.icon" />
                <span>{{ item.label }}</span>
            </router-link>

            <div class="nav-group-label">Business design</div>
        </nav>
        <DevNavigation v-if="mode === 'dev' && stek?.identity" />
        <c-stekz-tree
            v-else-if="stek?.identity"
            ref="stekztree"
            class="domains-tree"
            :class="{ 'domains-tree--nested': mode !== 'live' }"
            :data-stek-id="stek?.identity"
            :data-selected-domain-id="domain?.identity ?? ''"
            :data-selected-product-id="product?.identity ?? ''"
            :data-selected-process-id="process?.identity ?? ''"
            :data-mode="mode"
        ></c-stekz-tree>
        <div v-if="isMoving" class="moving-overlay-mask"></div>
        <div v-if="isMoving" class="moving-overlay">
            <ProgressSpinner />
        </div>
    </div>
    <div>
        <Menu v-if="subStageMenuItems.length > 0" :model="subStageMenuItems" />
    </div>

    <!-- Thread-branch worktree card — the primary, information-rich cue that the
         stek is being viewed at a thread's branch. Sits directly above the mode
         switcher so the "which worktree am I on" context lives with navigation. -->
    <div v-if="viewingThreadBranch" class="thread-worktree">
        <div class="thread-worktree__eyebrow">
            <svg
                class="thread-worktree__icon"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
            >
                <circle cx="6" cy="6" r="2.5" />
                <circle cx="6" cy="18" r="2.5" />
                <circle cx="18" cy="8" r="2.5" />
                <path d="M6 8.5v7" />
                <path d="M18 10.5a6 6 0 0 1-6 6H8.5" />
            </svg>
            On thread
        </div>
        <div class="thread-worktree__title">{{ branchTitle }}</div>
        <div v-if="branchSubtitle" class="thread-worktree__desc">
            {{ branchSubtitle }}
        </div>
        <!-- Conflict shortcut to the Reconcile page — mirrors the drawer's two
             states. Only shown when the branch actually has conflicts, so it
             stays a real signal rather than a permanent (often dead) button. -->
        <button
            v-if="pendingConflicts.length > 0"
            type="button"
            class="thread-worktree__conflicts thread-worktree__conflicts--pending"
            @click="goToReconcile"
        >
            <i class="pi pi-exclamation-triangle" />
            Resolve {{ pendingConflicts.length }} conflict{{
                pendingConflicts.length === 1 ? "" : "s"
            }}
        </button>
        <button
            v-else-if="resolvedConflicts.length > 0"
            type="button"
            class="thread-worktree__conflicts thread-worktree__conflicts--resolved"
            @click="goToReconcile"
        >
            <i class="pi pi-check-circle" />
            Review {{ resolvedConflicts.length }} resolved
        </button>
        <button
            type="button"
            class="thread-worktree__exit"
            @click="exitThreadBranchView"
        >
            <i class="pi pi-arrow-left" />
            Back to working copy
        </button>
    </div>

    <!-- mode -->
    <div id="mode">
        <!-- change mode button -->
        <Button
            type="button"
            :icon="modeIcon"
            class="p-button-text"
            :label="modeLabel"
            @mouseenter="toggle($event, 'modeMenu')"
            aria-haspopup="true"
            aria-controls="overlay_modemenu"
        />
        <!-- menu items of modes -->
        <Menu
            v-if="stek?.identity"
            ref="modeMenu"
            :model="modeMenuItems"
            :popup="true"
            id="overlay_modemenu"
        >
            <template #item="{ item, props }">
                <a
                    v-ripple
                    class="flex items-center"
                    :class="{
                        'active-mode': isActiveModeItem(item),
                    }"
                    v-bind="props.action"
                    v-tooltip.right="item.tooltip"
                    :style="
                        item.disabled
                            ? {
                                  'pointer-events': 'auto',
                                  'cursor': 'not-allowed',
                                  'opacity': '0.6',
                              }
                            : {}
                    "
                >
                    <span v-if="item.icon" :class="item.icon" />
                    <span>{{ item.label }}</span>
                    <span v-if="item.tag" class="tag">{{ item.tag }}</span>
                </a>
            </template>
        </Menu>
    </div>

    <!-- Delete confirmation -->
    <ConfirmationDialog
        :displayConfirmation="!!entityToDelete"
        :displaySpinner="isLoading"
        :message="`Are you sure you want to delete ${entityToDelete?.entityType} '${entityToDelete?.name}'?`"
        autofocus="close"
        @close-confirmation="entityToDelete = null"
        @success-confirm="deleteEntity"
    />

    <!-- Move confirmation -->
    <ConfirmationDialog
        :displayConfirmation="showMoveConfirmation"
        :displaySpinner="isLoading"
        :title="`Move '${entityMoveInfo?.entity.name}' to '${entityMoveInfo?.target.name}'`"
        :message="`Moving an entity to a different domain will reset it's history. Are you sure you want to proceed?`"
        autofocus="close"
        @close-confirmation="
            () => {
                showMoveConfirmation = false;
                entityMoveInfo = null;
                isMoving = false;
            }
        "
        @success-confirm="moveEntity"
    />
</template>

<script setup lang="ts">
import { whenever } from "@vueuse/core";

import type { MenuItem } from "primevue/menuitem";
import ProgressSpinner from "primevue/progressspinner";
import { useToast } from "primevue/usetoast";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import DevNavigation from "@/components/DevNavigation.vue";
import { resolveModeTarget } from "@/composables/mode-navigation";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { useThreadBranchView } from "@/composables/use-thread-branch-view";
import { useWorktreeManager } from "@/composables/worktree-manager";
import { diffStatusClasses } from "@/lib/diff-status";
import { api } from "@/lib/openapi";
import { withThreadRevision } from "@/lib/thread-revision-link";
import { baseSuccessToastMessage } from "@/primevue/init";
import ConfirmationDialog from "@/shared/ConfirmationDialog.vue";
import { useDomainsManager, useDomainsStore } from "@/stores/domains-store";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import {
    useProcessesManager,
    useProcessesStore,
} from "@/stores/processes-store";
import { useProductsManager, useProductsStore } from "@/stores/products-store";
import { useReleaseStore } from "@/stores/release-store";
import "@/wc/stekz-tree/index.js";

const route = useRoute();
const router = useRouter();
const toast = useToast();
const { stek } = useStekManager();
const { organization } = useOrganizationManager();
const { domain, product, process } = useCurrentEntityManager();

const { remove: removeDomain } = useDomainsStore();
const { isLoading: isDomainsLoading } = useDomainsManager();
const { remove: removeProduct } = useProductsStore();
const { isLoading: isProductsLoading } = useProductsManager();
const { remove: removeProcess } = useProcessesStore();
const { isLoading: isProcessesLoading } = useProcessesManager();
const { worktree } = useWorktreeManager();

// Thread-branch worktree card (rendered at the bottom of the sidebar).
const {
    viewingThreadBranch,
    branchTitle,
    branchSubtitle,
    pendingConflicts,
    resolvedConflicts,
    exitThreadBranchView,
    goToReconcile,
} = useThreadBranchView();

const isMoving = ref(false);

// `diffKey` maps each Strategy link to its field in the worktree's
// `strategy_diff`, so the link can carry the same added/modified/removed badge
// the entity tree uses. The backend only stamps these on a thread branch.
type StrategyDiffKey = "mission_vision" | "objectives" | "horizons";
const strategyNav: {
    name: string;
    label: string;
    icon: string;
    diffKey: StrategyDiffKey;
}[] = [
    {
        name: "strategyVision",
        label: "Vision & mission",
        icon: "pi pi-compass",
        diffKey: "mission_vision",
    },
    {
        name: "strategyObjectives",
        label: "Objectives",
        icon: "pi pi-objects-column",
        diffKey: "objectives",
    },
    {
        name: "strategyHorizons",
        label: "Horizons",
        icon: "pi pi-chart-line",
        diffKey: "horizons",
    },
];

// Thread diff badge for a Strategy link — empty (no class) when not viewing a
// thread, since the backend leaves `strategy_diff` all-null off a thread branch.
const strategyDiffClasses = (diffKey: StrategyDiffKey) =>
    diffStatusClasses({
        diff_status: worktree.value?.strategy_diff?.[diffKey],
    });
const navParams = computed(() => ({
    orgName: organization.value?.name,
    stekName: stek.value?.name,
}));

const mode = computed(() => {
    return route.matched.find((m) => m.meta.mode)?.meta.mode || "business";
});
const modeLabel = computed(() => {
    if (mode.value === "dev") return "IT Landscape";
    if (mode.value === "live") return "Live";
    return "Business";
});
const modeIcon = computed(() => {
    if (mode.value === "live") return "pi pi-play";
    if (mode.value === "dev") return "pi pi-code";
    return "pi pi-eye";
});
const isActiveModeItem = (item: MenuItem & { modeKey?: string }) =>
    item.modeKey === mode.value;
const isDevModeDisabled = computed(
    () => stek.value?.dev_mode_enabled === false
);

// In Live mode, a release must always be selected.
// Watch both mode and releases: handles mode switch, page refresh, and late-loading releases.
const releaseStore = useReleaseStore();

watch(
    [mode, () => releaseStore.releases],
    ([currentMode]) => {
        if (
            currentMode === "live" &&
            !releaseStore.activeRelease &&
            releaseStore.releases.length > 0
        ) {
            releaseStore.setActiveRelease(releaseStore.releases[0].name);
        }
    },
    { immediate: true }
);

const isLoading = computed(() => {
    return (
        isDomainsLoading.value ||
        isProductsLoading.value ||
        isProcessesLoading.value ||
        isMoving.value
    );
});

const modeMenuItems = computed<MenuItem[]>(() => {
    if (!organization.value?.name) {
        return [];
    }

    if (!stek.value?.name) {
        return [];
    }

    return [
        {
            label: "Business",
            icon: "pi pi-eye",
            modeKey: "business",
            command: () => {
                router.push(
                    resolveModeTarget(
                        "business",
                        route.name as string | null | undefined,
                        route.params,
                        {
                            orgName: organization.value?.name ?? "",
                            stekName: stek.value?.name ?? "",
                        }
                    )
                );
            },
        },
        {
            label: "IT Landscape",
            icon: "pi pi-code",
            tag: "ALPHA",
            modeKey: "dev",
            disabled: isDevModeDisabled.value,
            tooltip: isDevModeDisabled.value
                ? "Go to Stek Settings to enable Dev mode."
                : undefined,
            command: () => {
                if (isDevModeDisabled.value) return;

                // Dev intentionally does not preserve location — resolveModeTarget
                // has no dev entity routes, so it returns the dev landing page.
                router.push(
                    resolveModeTarget(
                        "dev",
                        route.name as string | null | undefined,
                        route.params,
                        {
                            orgName: organization.value?.name ?? "",
                            stekName: stek.value?.name ?? "",
                        }
                    )
                );
            },
        },
        {
            label: "Live",
            icon: "pi pi-play",
            tag: "BETA",
            modeKey: "live",
            disabled: stek.value?.live_mode_enabled === false,
            tooltip:
                stek.value?.live_mode_enabled === false
                    ? "Go to Stek Settings to enable Live mode."
                    : undefined,
            command: () => {
                router.push(
                    resolveModeTarget(
                        "live",
                        route.name as string | null | undefined,
                        route.params,
                        {
                            orgName: organization.value?.name ?? "",
                            stekName: stek.value?.name ?? "",
                        }
                    )
                );
            },
        },
    ];
});
const subStageMenuItems = computed<MenuItem[]>(() => {
    const items: MenuItem[] = [];

    const organizationName = organization.value?.name;
    if (!organizationName) {
        return [];
    }

    // Dashboard button removed - navigation is handled by LiveNavigation tree
    return items;
});

const menu = ref();
const modeMenu = ref();

watch(
    [mode, isDevModeDisabled],
    ([currentMode, devModeDisabled]) => {
        if (
            currentMode !== "dev" ||
            !devModeDisabled ||
            !organization.value?.name ||
            !stek.value?.name
        ) {
            return;
        }

        toast.add({
            severity: "info",
            summary: "IT Landscape Disabled",
            detail: "Returning to business.",
            life: 3000,
        });

        router.replace({
            name: "stekPage",
            params: {
                orgName: organization.value.name,
                stekName: stek.value.name,
            },
        });
    },
    { immediate: true }
);

const entityToDelete = ref();

const showMoveConfirmation = ref(false);
const entityMoveInfo = ref();

const stekztree = ref();

const onEntityMoved = (entityInfo) => {
    entityMoveInfo.value = entityInfo;

    const isCrossDomainMove =
        entityInfo.entityType === EntityType.PROCESS
            ? entityInfo.entity?.product?.domain?.identity !==
              entityInfo.target?.domain?.identity
            : entityInfo.entityType === EntityType.PRODUCT
              ? entityInfo.entity?.domain?.identity !==
                entityInfo.target?.identity
              : false;

    if (isCrossDomainMove) {
        // If the move is cross-domain, we need to show a confirmation dialog.
        showMoveConfirmation.value = true;
    } else {
        // If the move is not cross-domain, we can directly proceed with the move.
        showMoveConfirmation.value = false;
        moveEntity();
    }
};

onMounted(async () => {
    // Until we have integrated inline entity creation in the tree,
    // make use of creation modals.
    whenever(
        stekztree,
        () => {
            stekztree.value.addEventListener("delete", (e: CustomEvent) => {
                entityToDelete.value = e.detail;
            });
            stekztree.value.addEventListener("move", (e: CustomEvent) => {
                onEntityMoved(e.detail);
            });
            stekztree.value.addEventListener("reorder", (e: CustomEvent) => {
                const { entityType, ids } = e.detail;
                if (entityType === EntityType.DOMAIN) {
                    useDomainsStore().updateOrders(ids);
                } else if (entityType === EntityType.PRODUCT) {
                    useProductsStore().updateOrders(ids);
                } else if (entityType === EntityType.PROCESS) {
                    useProcessesStore().updateOrders(ids);
                }
            });
            stekztree.value.addEventListener(
                "toast-request",
                (e: CustomEvent) => {
                    toast.add({
                        ...baseSuccessToastMessage,
                        severity: e.detail.type,
                        summary: e.detail.message,
                    });
                }
            );
        },
        { immediate: true }
    );
});

const toggle = (event: Event, menuName: string) => {
    const activeEle = document.getElementsByClassName("active");
    if (activeEle.length > 0) {
        const activeEle = document.querySelector(".active");
        activeEle?.classList.remove("active");
    }
    switch (menuName) {
        case "menu":
            menu.value.toggle(event);
            break;
        case "modeMenu":
            modeMenu.value.toggle(event);
            break;
        default:
            // Menu not found;
            break;
    }
};

const moveEntity = async () => {
    try {
        isMoving.value = true;
        const { entity, target } = entityMoveInfo.value;
        if (entityMoveInfo.value.entityType === EntityType.PROCESS) {
            await api.startProcessProcessMgtMoveProcessStartPost({
                body: {
                    process_id: entity.identity,
                    target_product_id: target.identity,
                },
            });
        } else if (entityMoveInfo.value.entityType === EntityType.PRODUCT) {
            await api.startProductProductMgtMoveProductStartPost({
                body: {
                    product_id: entity.identity,
                    target_domain_id: target.identity,
                },
            });
        } else {
            console.error("Unknown entity type for move operation");
        }

        // Refresh the tree after successful move
        await stekztree.value?.refreshTree?.();
    } finally {
        isMoving.value = false;
        showMoveConfirmation.value = false;
        entityMoveInfo.value = null;
    }
};

const deleteEntity = async () => {
    if (!entityToDelete.value) return;
    if (!organization.value) return;

    const baseParams = {
        orgName: organization.value?.name,
        stekName: stek.value?.name,
    };

    if (entityToDelete.value.entityType === "domain") {
        await removeDomain(entityToDelete.value.identity);
        await router.push(
            withThreadRevision({
                name: "stekPage",
                params: {
                    ...baseParams,
                },
            })
        );
    } else if (entityToDelete.value.entityType === "product") {
        await removeProduct(entityToDelete.value.identity);
        await router.push(
            withThreadRevision({
                name: "domainPage",
                params: {
                    ...baseParams,
                    domainId: domain.value.identity,
                },
            })
        );
    } else if (entityToDelete.value.entityType === "process") {
        await removeProcess(entityToDelete.value.identity);
        await router.push(
            withThreadRevision({
                name: "productPage",
                params: {
                    ...baseParams,
                    productId: product.value.identity,
                },
            })
        );
    } else {
        console.error("Unknown entity type");
    }
    entityToDelete.value = null;
};
</script>

<style scoped lang="scss">
.custom-blockui-container {
    position: relative;
    display: block;
    height: 100%;
    overflow-y: auto;
}

c-stekz-tree {
    display: block;
    height: auto;
}

.strategy-nav {
    display: flex;
    flex-direction: column;
    padding: 4px 8px 0;
}

.nav-group-label {
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.04em;
    font-weight: 600;
    color: var(--text-color-secondary);
    padding: 14px 8px 4px;
}

.nav-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 8px;
    color: var(--text-color);
    text-decoration: none;
    cursor: pointer;
}
.nav-item:hover {
    background: var(--stekz-primary-background);
}
.nav-item--active {
    background: var(--p-highlight-background);
    color: var(--p-primary-color);
    font-weight: 600;
}
/* Thread diff indicators on a Strategy link, mirroring the <c-stekz-tree>
   entity badges: green = added, amber = modified, strikethrough = removed. */
.nav-item.diff-added {
    color: var(--stekz-diff-added-color, #16a34a);
}
.nav-item.diff-modified {
    color: var(--stekz-diff-modified-color, #d97706);
}
.nav-item.diff-removed {
    text-decoration: line-through;
    opacity: 0.6;
}
.nav-item__icon {
    flex: 0 0 auto;
    font-size: 1rem;
    width: 1.1em;
    text-align: center;
}

.domains-tree--nested {
    padding-left: 12px;
}

.moving-overlay-mask {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 999;
    background: rgba(0, 0, 0, 0.1);
    cursor: not-allowed;
}

.profile-link {
    color: unset;
    text-decoration: inherit;
}

// Thread-branch worktree card. Pink (secondary) accent + tint marks thread mode,
// matching the top-bar pill and accent strip; tokens keep it correct in dark mode.
.thread-worktree {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 8px;
    padding: 9px 11px;
    border-left: 3px solid var(--stekz-secondary-color);
    background: var(--stekz-secondary-background);
    font-size: 0.8rem;

    &__eyebrow {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        text-transform: uppercase;
        font-size: 0.62rem;
        font-weight: 700;
        letter-spacing: 0.05em;
        color: var(--stekz-secondary-color);
    }

    &__icon {
        flex: 0 0 auto;
    }

    &__title {
        font-weight: 600;
        line-height: 1.25;
        color: var(--text-color);
        // Clamp long thread titles to two lines.
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    &__desc {
        font-size: 0.72rem;
        line-height: 1.3;
        color: var(--text-color-secondary);
        // Keep the summary to a couple of lines so the card stays compact.
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    &__conflicts {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 5px;
        align-self: stretch;
        margin-top: 2px;
        padding: 5px 8px;
        border: 1px solid transparent;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.72rem;
        font-weight: 600;

        // Pending catch-up conflicts block Accept — flag them in the app's
        // "modified/attention" amber (same hue the diff badges use).
        &--pending {
            color: var(--stekz-diff-modified-color, #d97706);
            background: color-mix(
                in srgb,
                var(--stekz-diff-modified-color, #d97706) 12%,
                transparent
            );
            border-color: color-mix(
                in srgb,
                var(--stekz-diff-modified-color, #d97706) 45%,
                transparent
            );
        }

        // Resolved-but-not-accepted: a quieter "review your decisions" affordance.
        &--resolved {
            color: var(--text-color-secondary);
            background: var(--surface-background-mild);
            border-color: var(--stekz-outline-variant);
        }

        &:hover {
            filter: brightness(0.97);
        }
    }

    &__exit {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        align-self: flex-start;
        margin-top: 2px;
        padding: 2px 0;
        border: none;
        background: none;
        cursor: pointer;
        font-size: 0.72rem;
        color: var(--stekz-secondary-color);

        &:hover {
            text-decoration: underline;
        }
    }
}

#mode {
    width: 100%;

    button {
        width: 100%;
        text-transform: capitalize;
    }
}

.tag {
    font-size: 0.6rem;
    font-weight: 700;
    padding: 0.15em 0.4em;
    background: var(--p-primary-color);
    color: var(--p-primary-contrast-color);
    letter-spacing: 0.05em;
    margin-left: auto;
    align-self: center;
}

.active-mode {
    background-color: var(--p-highlight-background);
    color: var(--p-highlight-color);
    font-weight: 600;
}

.mode-content {
    /* Make sure the menu is above the button    */
    position: absolute;
    bottom: 50px;
}

.menu {
    border: none;
    width: fit-content;
}

.moving-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1000;
}

.moving-overlay p {
    margin: 0;
    color: var(--text-color);
    font-weight: 500;
}

.select-organization {
    text-decoration: none;
}
</style>
